import { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

import { useUserStore } from "@stores/index";
import {
  Button,
  DashboardSvg,
  CreditCardPaySvg,
  LicenseSvg,
  Briefcase2Svg,
  MapPinSvg,
  SquareRoundedPlusSvg,
  ReceiptRefundSvg,
  SquarePercentageSvg,
  MessageCircleSvg,
} from "@components/index";

export const NavPanelDesktop = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPage = location.pathname;
  const userId = useUserStore(({ userId }) => userId);
  const puEnabled = useUserStore(({ puEnabled }) => puEnabled);
  const disabledFeatures = useUserStore(
    ({ disabledFeatures }) => disabledFeatures,
  );
  const businesses = useUserStore(({ businesses }) => businesses);

  const items: {
    to: string;
    label: string;
    testId: string;
    image: ReactNode;
  }[] = [
    {
      to: "/",
      label: "Dashboard",
      testId: "navigation_dashboard",
      image: <DashboardSvg />,
    },
    {
      to: "/transactions",
      label: "Transactions",
      testId: "navigation_transactions",
      image: <CreditCardPaySvg />,
    },
    {
      to: "/submissions",
      label: "Submissions",
      testId: "navigation_submissions",
      image: <LicenseSvg />,
    },
    {
      to: "/privateUse",
      label: "Private Use",
      testId: "navigation_privateUse",
      image: <SquarePercentageSvg />,
    },
    {
      to: "/assets",
      label: "Assets",
      testId: "navigation_assets",
      image: <Briefcase2Svg />,
    },
    {
      to: "/mileage",
      label: "Mileage Log",
      testId: "navigation_mileage",
      image: <MapPinSvg />,
    },
    {
      to: "/queries",
      label: "Queries",
      testId: "navigation_queries",
      image: <MessageCircleSvg />,
    },
  ]
    .filter(
      ({ label }) =>
        (puEnabled &&
          !disabledFeatures.includes("Private Use") &&
          businesses.find((b) => b.type !== "landlord")) ||
        label !== "Private Use",
    )
    .filter(
      ({ label }) => !disabledFeatures.includes("Assets") || label !== "Assets",
    )
    .filter(
      ({ label }) =>
        !disabledFeatures.includes("Mileage Log") || label !== "Mileage Log",
    );

  const handleAddTransaction = () => {
    navigate("/transactions/create");
  };

  const handleAddRefund = () => {
    navigate("/transactions/refund");
  };

  return (
    !!userId && (
      <div className="flex items-center justify-between gap-x-10 bg-white-8 px-8 py-3">
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          {items.map(({ to, label, image, testId }) => (
            <Link
              data-testid={testId}
              key={to}
              to={to}
              className={clsx(
                "text-button",
                twMerge(
                  clsx(
                    "flex gap-x-1 text-white-100 hover:text-primary-200",
                    (to !== "/"
                      ? currentPage.includes(to)
                      : to === currentPage) && "text-primary-200",
                  ),
                ),
              )}
            >
              {image}
              {label}
            </Link>
          ))}
        </div>
        <div className="flex shrink-0 gap-x-8">
          <Button
            title="Add Refund"
            height="small"
            variant="text"
            dataTestid="button_topNavAddRefund"
            leftIcon={<ReceiptRefundSvg />}
            onClick={handleAddRefund}
          />
          <Button
            title="Add Transaction"
            height="small"
            dataTestid="button_addTransaction"
            leftIcon={<SquareRoundedPlusSvg />}
            onClick={handleAddTransaction}
          />
        </div>
      </div>
    )
  );
};
